/**
 * Admin → Tenants list (Phase 35.2, super-admin only).
 *
 * Route: /admin/tenants
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  listTenants,
  updateTenantStatus,
} from "../services/adminTenants.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

function statusBadge(status) {
  const variants = {
    active: "badge--success",
    suspended: "badge--warning",
    provisioning: "badge--info",
    provisioning_failed: "badge--error",
    deleted: "badge--error",
  };
  return (
    <span className={`badge ${variants[status] || ""}`}>{status}</span>
  );
}

export default function TenantsList() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suspending, setSuspending] = useState(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const pushToast = useStore((s) => s.pushToast);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    return listTenants({ includeDeleted })
      .then((data) => {
        setTenants(data.tenants || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [includeDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleStatusToggle(tenant) {
    const next = tenant.status === "suspended" ? "active" : "suspended";
    setSuspending(tenant.tenantId);
    try {
      await updateTenantStatus(tenant.tenantId, next);
      setTenants((list) =>
        list.map((t) =>
          t.tenantId === tenant.tenantId ? { ...t, status: next } : t
        )
      );
      pushToast(`Tenant ${next === "suspended" ? "suspended" : "reactivated"}`);
    } catch (err) {
      pushToast(`Error: ${err.message}`, "error");
    } finally {
      setSuspending(null);
    }
  }

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorBubble message={error} />;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1 className="admin-page__title">Tenants</h1>
        <div className="admin-page__header-actions">
          <label className="admin-page__toggle">
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
            />
            Show deleted
          </label>
          <Link to="/admin/tenants/new" className="btn-primary btn-sm">
            New tenant
          </Link>
        </div>
      </div>

      {tenants.length === 0 ? (
        <p className="admin-page__empty">No tenants found.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Domains</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.tenantId}>
                <td>
                  <Link
                    to={`/admin/tenants/${encodeURIComponent(t.tenantId)}`}
                    className="admin-table__link"
                  >
                    {t.name || t.tenantId}
                  </Link>
                  <div className="admin-table__sub">{t.tenantId}</div>
                </td>
                <td>{statusBadge(t.status)}</td>
                <td>{(t.domains || []).length}</td>
                <td>
                  {t.createdAt
                    ? new Date(t.createdAt).toLocaleDateString()
                    : "—"}
                </td>
                <td className="admin-table__actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      navigate(
                        `/admin/tenants/${encodeURIComponent(t.tenantId)}`
                      )
                    }
                  >
                    View
                  </button>
                  {t.status !== "provisioning" && t.status !== "deleted" && (
                    <button
                      className={`btn btn-sm ${
                        t.status === "suspended"
                          ? "btn-ghost"
                          : "btn-destructive"
                      }`}
                      disabled={suspending === t.tenantId}
                      onClick={() => handleStatusToggle(t)}
                    >
                      {t.status === "suspended" ? "Reactivate" : "Suspend"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
